import React, { useEffect, useState, useRef } from 'react';
import { Package, Search, Plus, Edit2, Trash2, X, AlertCircle, Upload, CheckCircle, AlertTriangle, FileDown, Download } from 'lucide-react';
import { getSpareparts, getSparepart, createSparepart, updateSparepart, deleteSparepart, deleteAllSpareparts, downloadSparepartTemplate, importSpareparts } from '../api';
import { useAuth } from '../AuthContext';
import * as XLSX from 'xlsx';
import ExportExcelModal from '../components/ExportExcelModal';

const emptyForm = {
  code: '',
  name: '',
  unit: '',
  price: '',
  warranty: '',
  stock: '',
};

export default function Spareparts() {
  const { user, hasMenuAction } = useAuth();
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingPart, setEditingPart] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [deletingAll, setDeletingAll] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const fileInputRef = useRef(null);

  const loadData = () => {
    setLoading(true);
    getSpareparts()
      .then(res => setParts(res.data))
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadData(); }, []);

  const filtered = parts.filter(p =>
    !search || p.name?.toLowerCase().includes(search.toLowerCase()) ||
    p.code?.toLowerCase().includes(search.toLowerCase())
  );

  const formatPrice = (price) => {
    const num = Number(price);
    if (isNaN(num) || num === 0) return 'Rp 0';
    return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(num);
  };

  // Susun baris export dari daftar sparepart
  const buildExportRows = (list) => list.map((p, i) => ({
    'No': i + 1,
    'Kode': p.code || '',
    'Nama Sparepart': p.name || '',
    'Satuan': p.unit || '',
    'Harga': Number(p.price) || 0,
    'Harga Beli': Number(p.harga_beli) || 0,
    'Harga Jual': Number(p.harga_jual) || 0,
    'Garansi': p.warranty || '',
    'Stok': Number(p.stock) || 0,
    'Tanggal Dibuat': p.created_at
      ? new Date(p.created_at).toLocaleDateString('id-ID', { day: '2-digit', month: '2-digit', year: 'numeric' })
      : '',
  }));

  // Tulis file Excel (.xlsx) dari daftar yang diberikan
  const doExportExcel = (list, suffix) => {
    try {
      if (!list || list.length === 0) {
        alert('Tidak ada data sparepart untuk di-export');
        return;
      }

      const rows = buildExportRows(list);
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(rows);

      // Atur lebar kolom
      ws['!cols'] = [
        { wch: 5 },   // No
        { wch: 16 },  // Kode
        { wch: 38 },  // Nama Sparepart
        { wch: 10 },  // Satuan
        { wch: 15 },  // Harga
        { wch: 15 },  // Harga Beli
        { wch: 15 },  // Harga Jual
        { wch: 14 },  // Garansi
        { wch: 9 },   // Stok
        { wch: 16 },  // Tanggal Dibuat
      ];

      XLSX.utils.book_append_sheet(wb, ws, 'Data Sparepart');

      const now = new Date();
      const filename = `Data_Sparepart${suffix ? '_' + suffix : ''}_${now.toISOString().slice(0, 10)}.xlsx`;
      XLSX.writeFile(wb, filename);
    } catch (err) {
      console.error('Export Excel gagal', err);
      alert('Gagal mengexport Excel. Silahkan coba lagi.');
    }
  };

  const openCreate = () => {
    setEditingPart(null);
    setForm(emptyForm);
    setError('');
    setShowModal(true);
  };

  const openEdit = async (part) => {
    setEditingPart(part);
    setError('');
    try {
      const res = await getSparepart(part.id);
      const p = res.data;
      setForm({
        code: p.code || '',
        name: p.name || '',
        unit: p.unit || '',
        price: p.price ? String(p.price) : '',
        warranty: p.warranty || '',
        stock: p.stock !== undefined && p.stock !== null ? String(p.stock) : '0',
      });
      setShowModal(true);
    } catch (err) {
      setError('Gagal memuat data sparepart');
    }
  };

  const handleSave = async () => {
    setError('');
    if (!form.name) { setError('Nama sparepart harus diisi'); return; }

    setSaving(true);
    try {
      const payload = {
        code: form.code,
        name: form.name,
        unit: form.unit,
        price: form.price ? parseFloat(form.price) : 0,
        warranty: form.warranty,
        stock: form.stock ? parseInt(form.stock) : 0,
      };

      if (editingPart) {
        await updateSparepart(editingPart.id, payload);
      } else {
        await createSparepart(payload);
      }
      setShowModal(false);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Gagal menyimpan sparepart');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (part) => {
    if (!window.confirm(`Yakin ingin menghapus sparepart "${part.name}"?`)) return;
    try {
      await deleteSparepart(part.id);
      loadData();
    } catch (err) {
      alert(err.response?.data?.error || 'Gagal menghapus sparepart');
    }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm('⚠️ PERINGATAN!\n\nAnda akan menghapus SEMUA data sparepart secara permanen.\nTindakan ini TIDAK dapat dibatalkan!\n\nApakah Anda yakin ingin melanjutkan?')) return;

    const secondConfirm = window.confirm('KONFIRMASI AKHIR\n\nApakah Anda benar-benar yakin ingin menghapus seluruh data sparepart?\n\nSemua data akan hilang permanen!');
    if (!secondConfirm) return;

    setDeletingAll(true);
    try {
      await deleteAllSpareparts();
      alert('✅ Semua data sparepart berhasil dihapus.');
      loadData();
    } catch (err) {
      alert(err.response?.data?.error || 'Gagal menghapus data sparepart');
    } finally {
      setDeletingAll(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await downloadSparepartTemplate();
      const url = window.URL.createObjectURL(new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'template_import_sparepart.xlsx');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download template gagal', err);
      alert('Gagal mendownload template. Silahkan coba lagi.');
    }
  };

  if (loading) return <div className="page-loading">Memuat data sparepart...</div>;

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.match(/\.(xlsx|xls)$/i)) {
      setError('File harus berformat Excel (.xlsx atau .xls)');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setImportResult(null);
    setShowImportModal(true);

    try {
      const res = await importSpareparts(file, (progressEvent) => {
        if (progressEvent.total) {
          setUploadProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
        }
      });
      const data = res.data;
      setImportResult(data);
      if (data.imported > 0) {
        loadData();
      }
    } catch (err) {
      setImportResult({
        error: err.response?.data?.error || err.message || 'Gagal mengupload file',
      });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div className="page spareparts-page">
      <div className="page-header">
        <div className="page-header-row">
          <div>
            <h2>Katalog Sparepart</h2>
            <p className="page-desc">{parts.length} item sparepart terdaftar</p>
          </div>
          <div className="page-header-actions">
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
            <button className="btn btn-secondary" onClick={handleDownloadTemplate}>
              <FileDown size={16} />
              <span>Download Template</span>
            </button>
            <button className="btn btn-secondary" onClick={handleImportClick}>
              <Upload size={16} />
              <span>Import Excel</span>
            </button>
            <button className="btn btn-secondary" onClick={() => setShowExportModal(true)}>
              <Download size={16} />
              <span>Export Excel</span>
            </button>
            {hasMenuAction('spareparts', 'create') && (
              <button className="btn btn-primary" onClick={openCreate}>
                <Plus size={16} />
                <span>Tambah Sparepart</span>
              </button>
            )}
            {user?.role === 'admin' && (
              <button
                className="btn btn-danger-outline"
                onClick={handleDeleteAll}
                disabled={deletingAll}
                title="Hapus semua data sparepart (hanya admin)"
                style={{
                  marginLeft: 8,
                  border: '1px solid var(--danger, #e74c3c)',
                  color: 'var(--danger, #e74c3c)',
                  background: 'transparent',
                }}
              >
                {deletingAll ? (
                  <span>Menghapus...</span>
                ) : (
                  <>
                    <Trash2 size={16} />
                    <span>Hapus Semua Data</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="search-box">
        <Search size={18} />
        <input
          type="text"
          placeholder="Cari sparepart..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="table-wrapper">
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ width: 40, textAlign: 'center' }}>No</th>
              <th>Kode</th>
              <th>Nama Sparepart</th>
              <th>Satuan</th>
              <th style={{ textAlign: 'right' }}>Harga</th>
              <th>Garansi</th>
              <th style={{ textAlign: 'center' }}>Stok</th>
              {(hasMenuAction('spareparts', 'edit') || hasMenuAction('spareparts', 'delete')) && (
                <th style={{ width: 80, textAlign: 'center' }}>Aksi</th>
              )}
            </tr>
          </thead>
          <tbody>
            {filtered.map((part, idx) => (
              <tr key={part.id}>
                <td style={{ textAlign: 'center', color: 'var(--gray-400)', fontSize: '0.78rem' }}>
                  {idx + 1}
                </td>
                <td><code style={{ fontSize: '0.8rem', background: 'var(--gray-100)', padding: '2px 6px', borderRadius: 4 }}>{part.code || '-'}</code></td>
                <td style={{ fontWeight: 500 }}>{part.name}</td>
                <td>{part.unit || '-'}</td>
                <td style={{ textAlign: 'right', fontWeight: 600, color: 'var(--gray-700)' }}>{formatPrice(part.price)}</td>
                <td>{part.warranty || '-'}</td>
                <td style={{ textAlign: 'center' }}>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    minWidth: 28, height: 24, padding: '0 8px',
                    borderRadius: 12,
                    fontSize: '0.78rem', fontWeight: 600,
                    background: (part.stock || 0) > 0 ? '#e8f5e9' : '#fbe9e7',
                    color: (part.stock || 0) > 0 ? '#2e7d32' : '#c62828',
                  }}>
                    {part.stock ?? 0}
                  </span>
                </td>
                {(hasMenuAction('spareparts', 'edit') || hasMenuAction('spareparts', 'delete')) && (
                  <td style={{ textAlign: 'center' }}>
                    <div className="action-btns" style={{ justifyContent: 'center' }}>
                      {hasMenuAction('spareparts', 'edit') && (
                        <button className="btn-icon" title="Edit" onClick={() => openEdit(part)}>
                          <Edit2 size={14} />
                        </button>
                      )}
                      {hasMenuAction('spareparts', 'delete') && (
                        <button className="btn-icon btn-danger" title="Hapus" onClick={() => handleDelete(part)}>
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={hasMenuAction('spareparts', 'edit') || hasMenuAction('spareparts', 'delete') ? 8 : 7} className="text-center text-muted">Tidak ada data sparepart</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Import Modal */}
      {showImportModal && (
        <div className="modal-overlay" onClick={() => { if (!uploading) setShowImportModal(false); }}>
          <div className="modal-content" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                <Upload size={18} style={{ marginRight: 8 }} />
                Import Data Sparepart
              </h3>
              {!uploading && (
                <button className="btn-icon" onClick={() => setShowImportModal(false)}>
                  <X size={18} />
                </button>
              )}
            </div>
            <div className="modal-body">
              {uploading ? (
                <div className="import-uploading">
                  <div className="import-progress-circle">
                    <Upload size={32} />
                  </div>
                  <p className="import-status-text">Mengupload & memproses file...</p>
                  <div className="import-progress-bar-track">
                    <div className="import-progress-bar-fill" style={{ width: `${uploadProgress}%` }} />
                  </div>
                  <p className="import-progress-pct">{uploadProgress}%</p>
                </div>
              ) : importResult ? (
                <div className="import-result">
                  {importResult.error ? (
                    <>
                      <div className="import-result-icon import-error-icon">
                        <AlertTriangle size={36} />
                      </div>
                      <h4>Import Gagal</h4>
                      <p className="import-error-msg">{importResult.error}</p>
                    </>
                  ) : (
                    <>
                      <div className="import-result-icon import-success-icon">
                        <CheckCircle size={36} />
                      </div>
                      <h4>Import Selesai!</h4>
                      <div className="import-stats">
                        <div className="import-stat-item">
                          <span className="import-stat-label">Diproses</span>
                          <span className="import-stat-value">{importResult.total || 0}</span>
                        </div>
                        <div className="import-stat-item import-stat-success">
                          <span className="import-stat-label">Berhasil</span>
                          <span className="import-stat-value">{importResult.imported || 0}</span>
                        </div>
                        <div className="import-stat-item import-stat-skipped">
                          <span className="import-stat-label">Diabaikan</span>
                          <span className="import-stat-value">{importResult.skipped || 0}</span>
                        </div>
                      </div>
                      {importResult.errors?.length > 0 && (
                        <div className="import-errors">
                          <p className="import-errors-title">
                            <AlertTriangle size={14} />
                            {importResult.errors.length} error
                          </p>
                          <ul>
                            {importResult.errors.slice(0, 5).map((err, i) => (
                              <li key={i}>{err}</li>
                            ))}
                            {importResult.errors.length > 5 && (
                              <li className="text-muted">...dan {importResult.errors.length - 5} lainnya</li>
                            )}
                          </ul>
                        </div>
                      )}
                    </>
                  )}
                </div>
              ) : null}
            </div>
            {!uploading && importResult && (
              <div className="modal-footer">
                <button className="btn btn-primary" onClick={() => { setShowImportModal(false); if (importResult?.imported > 0) loadData(); }}>
                  Tutup
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Export Excel Modal — pilih semua data / hasil filter */}
      <ExportExcelModal
        open={showExportModal}
        onClose={() => setShowExportModal(false)}
        title="Export Excel — Katalog Sparepart"
        allCount={parts.length}
        filteredCount={filtered.length}
        filterSummary={search ? [`Pencarian: "${search}"`] : []}
        onExportAll={() => { setShowExportModal(false); doExportExcel(parts, ''); }}
        onExportFiltered={() => { setShowExportModal(false); doExportExcel(filtered, 'Filter'); }}
      />

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                <Package size={18} style={{ marginRight: 8 }} />
                {editingPart ? 'Edit Sparepart' : 'Tambah Sparepart Baru'}
              </h3>
              <button className="btn-icon" onClick={() => setShowModal(false)}>
                <X size={18} />
              </button>
            </div>

            <div className="modal-body">
              {error && (
                <div className="form-error">
                  <AlertCircle size={14} />
                  <span>{error}</span>
                </div>
              )}

              <div className="form-grid">
                <div className="form-group">
                  <label>Kode Sparepart</label>
                  <input
                    type="text"
                    value={form.code}
                    onChange={e => setForm({ ...form, code: e.target.value })}
                    placeholder="Contoh: BRK-001"
                  />
                </div>
                <div className="form-group">
                  <label>Nama Sparepart *</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={e => setForm({ ...form, name: e.target.value })}
                    placeholder="Nama sparepart"
                  />
                </div>
                <div className="form-group">
                  <label>Satuan</label>
                  <input
                    type="text"
                    value={form.unit}
                    onChange={e => setForm({ ...form, unit: e.target.value })}
                    placeholder="Contoh: PCS, LBR, KG"
                  />
                </div>
                <div className="form-group">
                  <label>Harga (Rp)</label>
                  <input
                    type="text"
                    value={form.price ? Number(form.price).toLocaleString('id-ID') : ''}
                    onChange={e => setForm({ ...form, price: e.target.value.replace(/\D/g, '') })}
                    placeholder="0"
                    inputMode="numeric"
                  />
                </div>
                <div className="form-group">
                  <label>Garansi</label>
                  <input
                    type="text"
                    value={form.warranty}
                    onChange={e => setForm({ ...form, warranty: e.target.value })}
                    placeholder="Contoh: 1 bulan"
                  />
                </div>
                <div className="form-group">
                  <label>Stok</label>
                  <input
                    type="number"
                    value={form.stock}
                    onChange={e => setForm({ ...form, stock: e.target.value })}
                    placeholder="0"
                    min="0"
                  />
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Batal</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Menyimpan...' : 'Simpan'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
